import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../constants";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [productIds, setProductIds] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setProductIds([]);
      return;
    }
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.get(`${API}/wishlist/ids`, { headers });
      setProductIds(r.data.product_ids || []);
    } catch {
      // silent
    } finally {
      setLoaded(true);
    }
  }, [user, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isIn = (productId) => productIds.includes(productId);

  const toggle = async (productId) => {
    if (!user) {
      toast.error("Please log in to use the wishlist");
      return;
    }
    const wasIn = isIn(productId);
    // Optimistic update
    setProductIds((prev) =>
      wasIn ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (wasIn) {
        await axios.delete(`${API}/wishlist/${productId}`, { headers });
        toast.success("Removed from wishlist");
      } else {
        await axios.post(
          `${API}/wishlist`,
          { product_id: productId },
          { headers }
        );
        toast.success("Saved to wishlist");
      }
    } catch (e) {
      // Roll back on error
      setProductIds((prev) =>
        wasIn ? [...prev, productId] : prev.filter((id) => id !== productId)
      );
      toast.error("Couldn't update wishlist");
    }
  };

  return (
    <WishlistContext.Provider value={{ productIds, isIn, toggle, loaded, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
